/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { Log } from '../logging/Log';

export class Storage {
    TAG: string = 'Storage';

    static StorageType = {
        LOCAL_STORAGE: localStorage,
        SESSION_STORAGE: sessionStorage,
    };

    static Keys = {
        APP_CRASH_RETRY_LIMIT: 'APP_CRASH_RETRY_LIMIT',
        AUTH_CONFIGURATIONS: 'AUTH_CONFIGURATIONS',
        BODY_INFO_SERVICE_DATA: 'BODY_INFO_SERVICE_DATA',
        BODY_INFO_SERVICE_LAST_CALL_TIME: 'BODY_INFO_SERVICE_LAST_CALL_TIME',
        CA_DEVICE_ID: 'CA_DEVICE_ID',
        CCB_OVERRIDDEN_SWITCHES: 'CCB_OVERRIDDEN_SWITCHES',
        CCB_SWITCH_PARTNER: 'CCB_SWITCH_PARTNER',
        CCB_SWITCH_VERSION: 'CCB_SWITCH_VERSION',
        CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME: 'CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME',
        CHANNELS_SNAPSHOT_VERSION: 'CHANNELS_SNAPSHOT_VERSION',
        CHANNELS_TYPE: 'CHANNELS_TYPE',
        CHANNELS: 'CHANNELS',
        ENABLE_NETWORK_LOGS: 'ENABLE_NETWORK_LOGS',
        IN_HOME_LOCATION_CONSUMED_GRACE_PERIOD: 'IN_HOME_LOCATION_CONSUMED_GRACE_PERIOD',
        IN_HOME_LOCATION_DATA: 'IN_HOME_LOCATION_DATA',
        INTERNAL_RATINGS_SERVICE_DATA: 'INTERNAL_RATINGS_SERVICE_DATA',
        INTERNAL_RATINGS_SERVICE_LAST_CALL_TIME: 'INTERNAL_RATINGS_SERVICE_LAST_CALL_TIME',
        IS_APP_ALREADY_LAUNCHED: 'IS_APP_ALREADY_LAUNCHED',
        IS_VIDEO_DEBUG_OVERLAY_ACTIVE: 'IS_VIDEO_DEBUG_OVERLAY_ACTIVE',
        IS_VIDEO_WINDOW_ENABLED: 'IS_VIDEO_WINDOW_ENABLED',
        LANGUAGE: 'LANGUAGE',
        LAST_TUNED_CHANNEL: 'LAST_TUNED_CHANNEL',
        LICENSE_PLATE_AUTH_CODE: 'LICENSE_PLATE_AUTH_CODE',
        LOCALE: 'LOCALE',
        LOGGING_WINDOW: 'LOGGING_WINDOW',
        MSO_SERVICE_ID: 'MSO_SERVICE_ID',
        PLAYER_CC_LANGUAGE: 'PLAYER_CC_LANGUAGE',
        PLAYER_CC_STATUS: 'PLAYER_CC_STATUS',
        SAML_TOKEN_WITH_EXPIRY: 'SAML_TOKEN_WITH_EXPIRY',
        SLS_SERVICE_ENDPOINTS: 'SLS_SERVICE_ENDPOINTS',
        STATION_POLICIES: 'STATION_POLICIES',
        STATION_POLICY_SERVICE_LAST_CALL_TIME: 'STATION_POLICY_SERVICE_LAST_CALL_TIME',
        STATION_POLICY_SNAPSHOT_VERSION: 'STATION_POLICY_SNAPSHOT_VERSION',
        VIDEO_PROVIDER_CONFIGURATIONS_DATA: 'VIDEO_PROVIDER_CONFIGURATIONS_DATA',
        VIDEO_PROVIDER_CONFIGURATIONS_LAST_CALL_TIME:
            'VIDEO_PROVIDER_CONFIGURATIONS_LAST_CALL_TIME',
    };

    private static localStorage;

    private static sessionStorage;

    private storageType;

    private constructor(storageType) {
        this.storageType = storageType;
    }

    public static getInstance(storage = Storage.StorageType.LOCAL_STORAGE) {
        if (storage === Storage.StorageType.SESSION_STORAGE)
            return Storage.getInstanceSessionStorage();
        return Storage.getInstanceLocalStorage();
    }

    static getInstanceLocalStorage() {
        if (!Storage.localStorage)
            Storage.localStorage = new Storage(Storage.StorageType.LOCAL_STORAGE);
        return Storage.localStorage;
    }

    static getInstanceSessionStorage() {
        if (!Storage.sessionStorage)
            Storage.sessionStorage = new Storage(Storage.StorageType.SESSION_STORAGE);
        return Storage.sessionStorage;
    }

    static clearAll(preserveData?: string[]) {
        const preservedStates: { [key: string]: any } = {};
        preserveData?.forEach((key) => {
            preservedStates.key = Storage.getInstance().getItem(key);
        });
        Storage.sessionStorage?.clear();
        Storage.localStorage?.clear();
        preserveData?.forEach((key) => Storage.getInstance().setItem(key, preservedStates.key));
    }

    clearData() {
        const dataKeys = [
            Storage.Keys.CHANNELS,
            Storage.Keys.CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME,
            Storage.Keys.CHANNELS_SNAPSHOT_VERSION,
            Storage.Keys.IN_HOME_LOCATION_DATA,
            Storage.Keys.INTERNAL_RATINGS_SERVICE_DATA,
            Storage.Keys.STATION_POLICIES,
            Storage.Keys.STATION_POLICY_SNAPSHOT_VERSION,
            Storage.Keys.VIDEO_PROVIDER_CONFIGURATIONS_DATA,
        ];

        dataKeys.forEach((key) => {
            this.removeItem(key);
        });

        Storage.getInstance(Storage.StorageType.SESSION_STORAGE).clearSessionData();
    }

    clearSessionData() {
        const dataKeys = [Storage.Keys.BODY_INFO_SERVICE_DATA];

        dataKeys.forEach((key) => {
            this.removeItem(key);
        });
    }

    setItem(key: string, value: any): void {
        if (!this.storageType) {
            Log.e(this.TAG, 'Storage not initialized, create storage instance first');
            throw new Error('Storage not initialized, create storage instance first');
        }
        const data = {};
        data[key] = value;
        this.storageType.setItem(key, JSON.stringify(data));
    }

    removeItem(key: string): void {
        this.storageType.removeItem(key);
    }

    getItem(key: string): any {
        if (!this.storageType) {
            Log.e(this.TAG, 'Storage not initialized, create storage instance first');
            throw new Error('Storage not initialized, create storage instance first');
        }
        const data = this.storageType.getItem(key);
        if (!data) return null;
        const jsonData = JSON.parse(data);
        return jsonData[key];
    }

    getItemWithDefaultValue(key: string, defaultValue: any): any {
        const data = this.getItem(key);
        if (!data) return defaultValue;
        return data;
    }

    clear(): void {
        if (this.storageType) this.storageType.clear();
    }
}
